import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  HardDrive,
  Image as ImageIcon,
  Inbox,
  LayoutTemplate,
  Link2,
  LogOut,
  Megaphone,
  Menu,
  MessageCircleMore,
  Settings,
  Users as UsersIcon,
  Users2,
  Variable,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, getApiScopeOrg, setApiScopeOrg } from '@/api/client';
import { Badge } from './ui/badge';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from './ui/button';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ConnectionBadge } from './ConnectionBadge';

const COLLAPSED_KEY = 'rabitni.sidebar.collapsed';

const navItems = [
  { to: '/dashboard', label: 'nav.dashboard', icon: BarChart3, end: true },
  { to: '/campaigns', label: 'nav.campaigns', icon: Megaphone },
  { to: '/templates', label: 'nav.templates', icon: LayoutTemplate },
  { to: '/short-links', label: 'nav.short_links', icon: Link2 },
  { to: '/lists', label: 'nav.lists', icon: Users2 },
  { to: '/media', label: 'nav.media', icon: ImageIcon },
  { to: '/inbox', label: 'nav.inbox', icon: Inbox, badge: 'inbox' as const },
  { to: '/whatsapp', label: 'nav.whatsapp', icon: MessageCircleMore },
] as const;

const adminItems = [
  { to: '/users', label: 'nav.users', icon: UsersIcon },
  { to: '/variables', label: 'nav.variables', icon: Variable },
  { to: '/storage', label: 'nav.storage', icon: HardDrive },
  { to: '/settings', label: 'nav.settings', icon: Settings },
] as const;

const superAdminItems = [
  { to: '/organizations', label: 'nav.organizations', icon: Building2 },
] as const;

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = state.status === 'authenticated' ? state.user : null;
  const isRTL = i18n.resolvedLanguage === 'ar';

  // Super-admin can pin an org scope via ?organization_id=... in any
  // URL (the "View" button on the Organizations page uses this). We
  // capture it into the API client header and drop the query param so
  // subsequent navigation stays clean.
  const [scopeOrg, setScopeOrgState] = useState<string | null>(() => getApiScopeOrg());
  useEffect(() => {
    if (!user?.is_super_admin) {
      // Non-super-admins can't impersonate -- clear any stale value.
      if (scopeOrg) {
        setApiScopeOrg(null);
        setScopeOrgState(null);
      }
      return;
    }
    const incoming = searchParams.get('organization_id');
    if (incoming && incoming !== scopeOrg) {
      const next = incoming === user.organization_id ? null : incoming;
      setApiScopeOrg(next);
      setScopeOrgState(next);
      qc.invalidateQueries();
      const params = new URLSearchParams(searchParams);
      params.delete('organization_id');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, scopeOrg, user, qc, setSearchParams]);

  function clearScope() {
    setApiScopeOrg(null);
    setScopeOrgState(null);
    qc.invalidateQueries();
  }
  // Inbox needs to feel like a chat app — full viewport, no container,
  // edge-to-edge.
  const isInbox = location.pathname.startsWith('/inbox');

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {}
      return next;
    });
  }

  const inbox = useQuery({
    queryKey: ['inbox-badge'],
    queryFn: () => api.get<{ unread: number }>('/api/inbox?unread=true'),
    refetchInterval: 30_000,
    enabled: !!user,
  });

  const railWidth = collapsed ? 'w-16' : 'w-56';
  const mainOffset = collapsed ? 'md:ms-16' : 'md:ms-56';

  const renderNav = (item: {
    to: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    end?: boolean;
    badge?: string;
  }) => {
    const badgeCount =
      'badge' in item && item.badge === 'inbox' ? inbox.data?.unread ?? 0 : 0;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={'end' in item ? (item as any).end : false}
        onClick={() => setMobileOpen(false)}
        title={collapsed ? t(item.label) : undefined}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap',
            isActive
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground',
            collapsed && 'justify-center px-2',
          )
        }
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{t(item.label)}</span>
            {badgeCount > 0 && (
              <Badge variant="default" className="h-5 min-w-5 px-1.5 text-[10px]">
                {badgeCount}
              </Badge>
            )}
          </>
        )}
        {collapsed && badgeCount > 0 && (
          <span className="absolute top-1 end-1 h-2 w-2 rounded-full bg-primary" />
        )}
      </NavLink>
    );
  };

  const sidebarBody = (
    <div className="flex flex-col h-full">
      {/* Brand + collapse toggle — toggle is ALWAYS at the top of the
          sidebar so its position stays consistent regardless of collapsed
          state. When collapsed, the brand logo is hidden and only the
          toggle button takes the header slot. */}
      <div
        className={cn(
          'h-16 border-b flex items-center shrink-0',
          collapsed ? 'justify-center px-2' : 'gap-2 px-3',
        )}
      >
        {!collapsed && (
          <>
            <div className="brand-gradient h-9 w-9 rounded-lg shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-tight truncate">
                Rabitni <span className="text-muted-foreground">· رابطني</span>
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                {t('brand.tagline')}
              </div>
            </div>
          </>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="hidden md:flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/10 hover:text-foreground"
          title={collapsed ? t('nav.expand_sidebar', { defaultValue: 'Expand sidebar' }) : t('nav.collapse_sidebar', { defaultValue: 'Collapse sidebar' })}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? (isRTL ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />)
            : (isRTL ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />)}
        </button>
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {navItems.map(renderNav)}

        {(user?.role === 'admin' || user?.is_super_admin) && (
          <>
            <div
              className={cn(
                'mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
                collapsed && 'text-center px-0',
              )}
            >
              {collapsed ? '·' : t('nav.admin', { defaultValue: 'Admin' })}
            </div>
            {adminItems.map(renderNav)}
          </>
        )}

        {user?.is_super_admin && (
          <>
            <div
              className={cn(
                'mt-4 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
                collapsed && 'text-center px-0',
              )}
            >
              {collapsed ? '·' : t('nav.super_admin', { defaultValue: 'Super admin' })}
            </div>
            {superAdminItems.map(renderNav)}
          </>
        )}
      </nav>

      {/* Footer: connection / language / sign-out — no toggle here anymore;
          the collapse/expand button lives in the top header for consistency. */}
      <div className="border-t p-2 space-y-1 shrink-0">
        {!collapsed && (
          <div className="px-1 py-1">
            <ConnectionBadge />
          </div>
        )}
        <div className={cn('flex items-center gap-1', collapsed && 'flex-col')}>
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="sm"
            className={cn('justify-start', collapsed ? 'w-9 px-0 justify-center' : 'flex-1')}
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            title={collapsed ? t('nav.logout') : undefined}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="ms-2">{t('nav.logout')}</span>}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      <a href="#main-content" className="skip-link">
        {t('common.skip_to_content', { defaultValue: 'Skip to content' })}
      </a>
      {/* Mobile top bar — only on small screens */}
      <header className="md:hidden sticky top-0 z-40 h-14 border-b bg-background/95 backdrop-blur flex items-center justify-between px-3">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-accent/10"
          aria-label={t('common.open_menu', { defaultValue: 'Open menu' })}
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="brand-gradient h-7 w-7 rounded-md" />
          <span className="text-sm font-semibold">Rabitni</span>
        </div>
        <div className="w-9" />
      </header>

      {/* Fixed desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex fixed inset-y-0 start-0 z-30 flex-col border-e bg-background transition-[width] duration-200',
          railWidth,
        )}
      >
        {sidebarBody}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="md:hidden fixed inset-y-0 start-0 z-50 w-64 bg-background border-e flex flex-col">
            <div className="h-12 border-b flex items-center justify-end px-2">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-accent/10"
                aria-label={t('common.close_menu', { defaultValue: 'Close menu' })}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {sidebarBody}
          </aside>
        </>
      )}

      {/* Main area — shifted by sidebar width on desktop */}
      <div className={cn('min-h-screen flex flex-col', mainOffset)}>
        {/* Super-admin impersonation banner. Always visible while pinned
            to a non-self org so the operator can't forget they're viewing
            customer data. Click "Exit" to bounce back to their own org. */}
        {user?.is_super_admin && scopeOrg && (
          <div className="sticky top-0 z-20 bg-amber-500 text-amber-950 px-4 py-2 text-xs font-medium flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" />
              {t('orgs.viewing_as', {
                defaultValue: 'Viewing organization {{org}} as super admin',
                org: scopeOrg,
              })}
            </span>
            <button
              type="button"
              onClick={clearScope}
              className="rounded bg-amber-950/10 px-2 py-0.5 hover:bg-amber-950/20"
            >
              {t('orgs.exit_scope', { defaultValue: 'Exit and return to my workspace' })}
            </button>
          </div>
        )}
        <main
          id="main-content"
          className={cn(
            'flex-1 min-w-0',
            isInbox ? 'p-3' : 'container py-6',
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
